import { useEffect } from 'react'
import { useLocation } from 'router-v6'
import { Card, CardHeader, CardTitle, CardContent } from '@neo/primitives/card'
import { Typography } from '@neo/primitives/typography'
import { Download, AlertCircle } from 'lucide-react'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import type { ApiResponse } from '@shared/types'
import type { AxiosError } from 'axios'
import { useDownloadFile } from './queries'
import { SuspenseLoader } from '../../shared/components/suspense-loader'

export const DownloadFile = () => {
  const location = useLocation()
  const fileNameMatch = location.pathname.match(/^\/download\/(.+)$/)
  const fileName = fileNameMatch ? fileNameMatch[1] : ''
  const { data, isLoading, error } = useDownloadFile(fileName)
  const errorHeadingMap = {
    'Not found': 'File Not Found',
    'Link expired': 'Link Expired',
    'Access denied': 'Access Denied',
  }

  const errorHeading =
    error &&
    errorHeadingMap[
      getErrorText(error as AxiosError<ApiResponse<unknown>>) as keyof typeof errorHeadingMap
    ]

  useEffect(() => {
    if (!data?.data?.download_link) return

    // Create a link element and trigger download
    const link = document.createElement('a')
    // Ensure the download link uses + instead of encoded spaces
    link.href = data.data.download_link
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [data, fileName])

  if (!fileName) {
    return (
      <div className="min-h-screen grid place-items-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Invalid Download URL
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Typography variant="P/400">
              The download URL appears to be invalid. Please check the URL and try again.
            </Typography>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) return <SuspenseLoader text={`Downloading ${fileName}...`} />

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {error ? (
              <AlertCircle className="h-5 w-5 text-destructive" />
            ) : (
              <Download className="h-5 w-5" />
            )}
            {error ? errorHeading || 'Download Failed' : 'Downloading File'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="space-y-4">
              <Typography variant="P/400">
                Sorry, we couldn&apos;t download <span className="font-medium">{fileName}</span>.
              </Typography>
              <Typography variant="S/400" className="text-muted-foreground">
                Please try again later or contact support if the problem persists.
              </Typography>
            </div>
          ) : (
            <Typography variant="P/400">
              Your download of <span className="font-medium">{fileName}</span> should start
              automatically. If it doesn&apos;t start shortly,{' '}
              <a
                href={data?.data?.download_link?.replace(/%20/g, '+')}
                download={fileName}
                data-testid="link-manual-download"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                click here to download
              </a>
              .
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
